export const toCamelCase = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      if (index === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('');
}

export const sanitizePhoneInput = (value, options = {}) => {
  const { allowLeadingPlus = true } = options

  if (value === null || value === undefined) return ''

  const raw = String(value).replace(/\s+/g, '')
  if (!raw) return ''

  if (!allowLeadingPlus) {
    return raw.replace(/\D/g, '')
  }

  const hasLeadingPlus = raw.startsWith('+')
  const stripped = raw.replace(/[^\d+]/g, '')

  if (!hasLeadingPlus) {
    return stripped.replace(/\D/g, '')
  }

  const numericPortion = stripped.slice(1).replace(/\D/g, '')
  return numericPortion ? `+${numericPortion}` : '+'
}

export const getOptionIdentifier = (option) => {
  if (option === null || option === undefined || option === '') return undefined
  if (typeof option !== 'object') return option
  return option.value ?? option.id ?? option.code ?? option.uuid ?? option.name ?? option.label
}

export const findOptionByValue = (options, value) => {
  const identifier = getOptionIdentifier(value)
  if (identifier === undefined) return null
  return options.find((opt) => {
    const optionIdentifier = getOptionIdentifier(opt)
    if (optionIdentifier === identifier) return true

    const candidates = [
      opt?.value,
      opt?.id,
      opt?.code,
      opt?.uuid,
      opt?.name,
      opt?.label,
    ].filter((entry) => entry !== undefined && entry !== null && entry !== '')

    return candidates.includes(identifier)
  }) || null
}

export const resolveOptionValue = (options, value, fallback) => {
  const identifier = getOptionIdentifier(value)
  if (identifier === undefined) return fallback
  const matched = findOptionByValue(options, identifier)
  return getOptionIdentifier(matched) ?? identifier ?? fallback
}

const normalizeTariffUnitIdentifier = (value) => {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

const collectTariffUnitCandidates = (value) => {
  if (value === null || value === undefined || value === '') return []

  const rawEntries =
    typeof value === 'object'
      ? [
          value.id,
          value.internalId,
          value.value,
          value.code,
          value.uuid,
          value.name,
          value.label,
          value.unit_code,
          value.unit_head,
        ]
      : [value]

  const candidates = new Set()

  rawEntries
    .filter((entry) => entry !== undefined && entry !== null && entry !== '')
    .forEach((entry) => {
      const numeric = toNumericIdentifier(entry)
      if (numeric !== null) {
        candidates.add(`number:${numeric}`)
      }

      const normalized = normalizeTariffUnitIdentifier(entry)
      if (normalized) {
        candidates.add(`text:${normalized}`)
      }
    })

  return [...candidates]
}

const toNumericIdentifier = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value)
  }

  const normalized = normalizeTariffUnitIdentifier(value)
  if (/^\d+$/.test(normalized)) {
    return Number(normalized)
  }

  return null
}

export const resolveTariffUnitManagementId = (value, fallback = null) => {
  if (typeof value === 'object' && value !== null) {
    const objectId = [
      value.id,
      value.internalId,
      value.value,
      value.code,
      value.unit_code,
    ]
      .map((entry) => toNumericIdentifier(entry))
      .find((entry) => entry !== null)

    return objectId ?? fallback
  }

  return toNumericIdentifier(value) ?? fallback
}

export const findTariffUnitManagementOptionByValue = (options, value) => {
  const targetCandidates = new Set(collectTariffUnitCandidates(value))
  if (!targetCandidates.size) return null

  return (
    options.find((option) =>
      collectTariffUnitCandidates(option).some((candidate) =>
        targetCandidates.has(candidate),
      ),
    ) || null
  )
}

export const resolveCarTonsValue = (value, fallback = null) => {
  return resolveTariffUnitManagementId(value, fallback)
}

export const findCarTonsOptionByValue = (options, value) => {
  return findTariffUnitManagementOptionByValue(options, value)
}

export const buildRequiredMessage = (label, t) => {
  const translation = t('validation.requiredField', { field: label })
  if (translation && translation !== 'validation.requiredField') {
    return translation
  }

  const suffix = t('general.isRequired')
  if (suffix && suffix !== 'general.isRequired') {
    return `${label} ${suffix}`
  }

  return `${label} is required`
}

export const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

export const formatDateTime = (dateString) => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  } catch (error) {
    return ''
  }
}


export const formatCurrency = (value, withPrefix = true) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return withPrefix ? 'Rp0' : '0';
  }

  const numeric = typeof value === 'string' ? Number(value) : value;

  const formatted = new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(numeric);

  // Hapus spasi antara Rp dan angka
  return withPrefix ? formatted.replace(/\s/g, '') : formatted.replace(/[^\d.,]/g, '');
}

export const parseCurrency = (formatted) => {
  if (!formatted) return 0;
  return Number(formatted.replace(/[^0-9]/g, '')) || 0;
}

export const isValidContainerNumber = (value) => {
  const regex = /^[A-Z]{4}\s?\d{6}\s?\d$/;
  return regex.test(value.toUpperCase());
};

export const buildFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;

  return `${import.meta.env.VITE_API_BASE_URL}${path}`;
};
